import { Box, Typography, styled } from "@mui/material";
import { CarDownloadButton, CarLoadingButton } from "@app/components/Buttons";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import { PortfolioGroupAssetAllocationTable } from "../PortfolioGroupAssetAllocationTable";
import { PortfolioGroupAssetStatTable } from "../PortfolioGroupAssetStatTable";
import { PortfolioGroupAssetStatChart } from "../PortfolioGroupAssetStatChart";
import { useStatAxisSelector } from "./useStatAxisSelector";
import { assetAllocationPages } from "@app/const/route.const";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "@app/hooks/useAppDispatch";
import { useState } from "react";
import { showToast } from "@app/redux/toastSlice";
import { useEditPortfolioGroupPublish } from "./useEditPortfolioGroupPublish";
import { CarSwitch } from "@app/components/Switch";
import { useExportAssetAllocation } from "../useExportAssetAllocation";
import { InternalUseWithDisclosures } from "./InternalUse";
import { CarPageContent } from "@app/components/PageContent";
import {
  CarFlatPageControl,
  CarFlatPageItem,
} from "@app/components/FlatPageControl";
import { StatAxisSelector } from "./StatAxisSelector";
import { CarPageContentCode, usePageContent } from "@app/hooks/usePageContent";
import { CarTooltipBox } from "@app/components/TooltipBox";
import { pendoClasses } from "@app/core/thirdParty/pendo";
import { AssetClassStatisticsTable } from "./AssetClassStatisticsTable";
import { useInteractiveSimulationModeSelector } from "./useInteractiveSimulationModeSelector";
import { InteractiveSimulationModeSelector } from "./InteractiveSimulationModeSelector";
// import { openInNewTab } from "@app/utils";

const StyledPage = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  paddingTop: theme.spacing(5),
  paddingBottom: theme.spacing(5),
  paddingLeft: theme.spacing(4.25),
  paddingRight: theme.spacing(4.25),
  backgroundColor: theme.palette.gray1,
  borderRadius: "5px",
  borderColor: theme.palette.gray6,
  borderStyle: "solid",
  borderWidth: "1px",
}));

export const EditPortfolioGroupPublish = (props: { isReadOnly?: boolean }) => {
  const data = useEditPortfolioGroupPublish();
  const statAxis = useStatAxisSelector();

  const navigate = useNavigate();
  const [isPublishing, setIsPublishing] = useState(false);
  const dispatch = useAppDispatch();
  const pageContent = usePageContent();

  const interactiveSimulationModeSelector =
    useInteractiveSimulationModeSelector({
      statGroups: data.statTableData.statGroups.filter((i) => !i.isHistorical),
    });

  const [currentTab, setCurrentTab] = useState<string | undefined>("assets");

  const hasErrors = data.tableData.bands.some((b) =>
    b.columns.some((c) => c.hasError),
  );

  const showErrorToast = (message: string) =>
    dispatch(
      showToast({
        kind: "error",
        message,
      }),
    );

  const handlePublish = async () => {
    if (hasErrors) {
      showErrorToast("Please fix asset allocation errors first");
      return;
    }

    setIsPublishing(true);
    try {
      const publishResult = await data.handlePublish();
      if (!("error" in publishResult)) {
        navigate(assetAllocationPages.getPortfolioGroupsUrl());
      }
    } finally {
      setIsPublishing(false);
    }
  };

  const exportAssetAllocation = useExportAssetAllocation();

  const handleExport = () => {
    exportAssetAllocation({
      tableData: data.tableData,
    });
  };

  // YS todo check why this is commented out

  // const handleShowPdf = () => {
  //   if (data.portfolioGroupId && data.portfolioGroupVersionId) {
  //     openInNewTab(
  //       assetAllocationPages.getPdfPortfolioGroupPublishReportUrl({
  //         portfolioGroupId: data.portfolioGroupId,
  //         portfolioGroupVersionId: data.portfolioGroupVersionId,
  //         xStatId: statAxis.xStatId,
  //         yStatId: statAxis.yStatId,
  //       }),
  //     );
  //   }
  // };

  return (
    <TablePlaceHolder
      isLoading={data.isLoading || pageContent.isLoading}
      rows={8}
      sx={{ mt: 5 }}
    >
      <Typography variant="h2SSemiBold" sx={{ mt: 4 }}>
        Publish
      </Typography>
      <CarPageContent
        sx={{ mt: 3 }}
        content={pageContent.getContent(
          CarPageContentCode.ANALYZE_PORTFOLIO_GROUP_PUBLISHED_PORTFOLIOS,
        )}
      />
      <Box sx={{ display: "flex", alignItems: "flex-end" }}>
        <StatAxisSelector
          statAxisSelector={statAxis}
          sx={{ mt: 5 }}
          isWhiteContext
          items={data.statTableData.statGroups.flatMap((i) => i.rows)}
        />
        {/* <CarDownloadButton onClick={handleShowPdf} sx={{ ml: "auto" }}>
          Download Reports
        </CarDownloadButton> */}
      </Box>
      <Box
        sx={{
          mt: 5,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "gray1",
          border: "1px solid",
          borderColor: "gray3",
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignSelf: "center",
          }}
        >
          <InternalUseWithDisclosures sx={{ mr: 2 }} />
          <PortfolioGroupAssetStatChart
            statTableData={data.statTableData}
            {...statAxis}
          />
        </Box>
      </Box>
      <CarFlatPageControl
        sx={{
          mt: 7,
          ".flat-page-control-header": {
            ml: 3,
            gap: 3,
          },
          ".flat-page-control-button": {
            p: 1,
            fontSize: "18px",
          },
        }}
        items={[
          {
            label: "Assets",
            value: "assets",
            pendoClassName: pendoClasses.editPortfolioEditCompareTabAssets,
            content: (
              <StyledPage>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    columnGap: 5,
                    rowGap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="par02Regular">
                      Show Input Only
                    </Typography>
                    <CarTooltipBox
                      sx={{ ml: 0.5 }}
                      className={pendoClasses.editPortfolioPublishShowInputOnly}
                    />
                    <CarSwitch
                      size="small"
                      checked={!data.isTreeView}
                      onChange={(e, value) => data.setIsTreeView(!value)}
                    />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Typography variant="par02Regular">Existing</Typography>
                    <CarSwitch
                      size="small"
                      checked={data.showBenchmark}
                      onChange={(e, value) => data.setShowBenchmark(value)}
                    />
                    <Typography variant="par02Regular">Benchmark</Typography>
                    <CarTooltipBox
                      sx={{ ml: -1 }}
                      className={
                        pendoClasses.editPortfolioPublishExistingBenchmark
                      }
                    />
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="par02Regular">
                      Hide Empty Values
                    </Typography>
                    <CarTooltipBox
                      sx={{ ml: 0.5 }}
                      className={
                        pendoClasses.editPortfolioPublishHideEmptyValues
                      }
                    />
                    <CarSwitch
                      size="small"
                      checked={data.hideZeroValues}
                      onChange={(e, value) => data.setHideZeroValues(value)}
                    />
                  </Box>
                  <InteractiveSimulationModeSelector
                    sx={{ mt: 0.5 }}
                    selector={interactiveSimulationModeSelector}
                  />
                  <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
                    <CarDownloadButton onClick={handleExport}>
                      Download
                    </CarDownloadButton>
                  </Box>
                </Box>

                <PortfolioGroupAssetAllocationTable
                  sx={{ mt: 3 }}
                  storageKey={data.portfolioGroupId}
                  tableData={data.tableData}
                  onAllocationChange={async () => {}}
                  onModuleRename={() => Promise.resolve()}
                  onModuleDelete={() => {}}
                  onModuleMove={() => {}}
                  isReadOnly={true}
                  hideZeroValues={data.hideZeroValues}
                  isTreeView={data.isTreeView}
                  headerStatRows={
                    interactiveSimulationModeSelector.selectedStatRows
                  }
                  isStatFetching={data.statTableData.isFetching}
                />
              </StyledPage>
            ),
          },
          ...data.statTableData.statGroups
            .filter((i) => !i.isHistorical)
            .map<CarFlatPageItem<string>>((group) => ({
              label: group.label,
              pendoClassName:
                pendoClasses.analyzePortfolioGroupEditCompareTabStat(
                  group.code,
                ),
              value: group.code,
              content: (
                <StyledPage>
                  <PortfolioGroupAssetStatTable
                    tableData={data.tableData}
                    rows={group.rows}
                    showPercentile={group.code === "SIMULATION"}
                  />
                </StyledPage>
              ),
            })),
          {
            label: "Asset Class Statistics",
            value: "assetClassStatistics",
            pendoClassName:
              pendoClasses.editPortfolioEditCompareTabAssetClassStatistics,
            content: (
              <StyledPage>
                <AssetClassStatisticsTable
                  simulationGroupName={data.simulationGroupName}
                  items={data.assetClassStatisticsItems}
                />
              </StyledPage>
            ),
          },
        ]}
        value={currentTab}
        onChange={setCurrentTab}
      />
      {!props.isReadOnly && (
        <Box
          sx={{ mt: 5, display: "flex", gap: 2.5, justifyContent: "center" }}
        >
          <CarLoadingButton
            sx={{ fontSize: "18px" }}
            isLoading={isPublishing}
            variant="contained"
            onClick={handlePublish}
          >
            Publish
          </CarLoadingButton>
        </Box>
      )}
    </TablePlaceHolder>
  );
};
